import { createPermission } from '@backstage/plugin-permission-common';

/**
 * Every action in the console that access can be granted or withheld for.
 *
 * Named `<feature>.<resource>.<action>` to match the convention Backstage's
 * own plugins use, so a grant list reads consistently whether the permission
 * came from here or from the catalog.
 *
 * Read is deliberately separate from create and delete everywhere. The most
 * common real request — "let them look, but not change anything" — should be
 * two clicks, not a special case.
 */

const read = (name: string) =>
  createPermission({ name, attributes: { action: 'read' } });
const create = (name: string) =>
  createPermission({ name, attributes: { action: 'create' } });
const del = (name: string) =>
  createPermission({ name, attributes: { action: 'delete' } });
const update = (name: string) =>
  createPermission({ name, attributes: { action: 'update' } });

// ---------------------------------------------------------------- databases
export const databaseReadPermission = read('database.instance.read');
export const databaseCreatePermission = create('database.instance.create');
export const databaseDeletePermission = del('database.instance.delete');
/** Renaming, importing a dump, granting roles, running SQL. */
export const databaseAdministerPermission = update('database.instance.administer');

// ------------------------------------------------------------- environments
export const environmentReadPermission = read('environment.instance.read');
export const environmentCreatePermission = create('environment.instance.create');
export const environmentDeletePermission = del('environment.instance.delete');
/**
 * Changing things from inside an environment: kubectl commands that write,
 * and downloading the kubeconfig that lets someone do the same from outside.
 * Reading what an environment is running stays under `read`.
 */
export const environmentExecPermission = update('environment.instance.exec');

// ----------------------------------------------------------------- workloads
export const workloadReadPermission = read('workload.instance.read');
export const workloadCreatePermission = create('workload.instance.create');
export const workloadDeletePermission = del('workload.instance.delete');

// ------------------------------------------------------------------ clusters
export const clusterReadPermission = read('cluster.instance.read');
export const clusterCreatePermission = create('cluster.instance.create');
export const clusterDeletePermission = del('cluster.instance.delete');

// ------------------------------------------------------------------- add-ons
export const addonReadPermission = read('addon.instance.read');
export const addonCreatePermission = create('addon.instance.create');

// ---------------------------------------------------------------- workflows
export const workflowReadPermission = read('workflow.instance.read');
export const workflowCreatePermission = create('workflow.instance.create');

// --------------------------------------------------------------------- cost
export const costReadPermission = read('cost.report.read');

// -------------------------------------------------------------- credentials
export const credentialsReadPermission = read('credentials.secret.read');
export const credentialsWritePermission = update('credentials.secret.write');

// ----------------------------------------------------------------- activity
export const activityReadPermission = read('activity.event.read');

// ------------------------------------------------------------------ pocteo ai
export const aiUsePermission = update('ai.chat.use');

// --------------------------------------------------------------- scorecards
export const scorecardReadPermission = read('scorecard.report.read');

// ------------------------------------------------------------- self-service
export const selfServiceReadPermission = read('selfservice.template.read');

// -------------------------------------------------------------- catalog page
/**
 * Distinct from Backstage's own `catalog.entity.read`, which governs entity
 * data everywhere it is used. This one only decides whether the catalog
 * appears in the console's own navigation.
 */
export const catalogPageReadPermission = read('catalog.page.read');

// --------------------------------------------------------------------- rbac
/** Seeing who has access to what. */
export const rbacReadPermission = read('rbac.role.read');
/** Changing it. This is the permission that hands out permissions. */
export const rbacManagePermission = update('rbac.role.manage');

/**
 * Permissions defined by Backstage's own plugins that this console governs.
 *
 * Declared with the upstream names so the policy's decision is the one those
 * plugins act on. They are here because the alternative is worse: the policy
 * lets unrecognised permissions through to the plugin that owns them, which
 * is right for a read, and quite wrong for "provision infrastructure" and
 * "delete this entity". Left ungoverned, every role would inherit them by
 * simply never having been told not to.
 *
 * Being governed means denied unless a role grants them.
 */
export const templateExecutePermission = createPermission({
  name: 'scaffolder.task.create',
  attributes: { action: 'create' },
});
export const templateActionExecutePermission = createPermission({
  name: 'scaffolder.action.execute',
  attributes: { action: 'create' },
});
export const templateCancelPermission = createPermission({
  name: 'scaffolder.task.cancel',
  attributes: { action: 'update' },
});
export const catalogEntityCreatePermission = createPermission({
  name: 'catalog.entity.create',
  attributes: { action: 'create' },
});
export const catalogEntityDeletePermission = createPermission({
  name: 'catalog.entity.delete',
  attributes: { action: 'delete' },
});
export const catalogLocationCreatePermission = createPermission({
  name: 'catalog.location.create',
  attributes: { action: 'create' },
});
export const catalogLocationDeletePermission = createPermission({
  name: 'catalog.location.delete',
  attributes: { action: 'delete' },
});

export const POCTEO_PERMISSIONS = [
  templateExecutePermission,
  templateActionExecutePermission,
  templateCancelPermission,
  catalogEntityCreatePermission,
  catalogEntityDeletePermission,
  catalogLocationCreatePermission,
  catalogLocationDeletePermission,
  selfServiceReadPermission,
  catalogPageReadPermission,
  databaseReadPermission,
  databaseCreatePermission,
  databaseDeletePermission,
  databaseAdministerPermission,
  environmentReadPermission,
  environmentCreatePermission,
  environmentDeletePermission,
  environmentExecPermission,
  workloadReadPermission,
  workloadCreatePermission,
  workloadDeletePermission,
  clusterReadPermission,
  clusterCreatePermission,
  clusterDeletePermission,
  addonReadPermission,
  addonCreatePermission,
  workflowReadPermission,
  workflowCreatePermission,
  costReadPermission,
  credentialsReadPermission,
  credentialsWritePermission,
  activityReadPermission,
  aiUsePermission,
  scorecardReadPermission,
  rbacReadPermission,
  rbacManagePermission,
];
